import { defineComponent as _defineComponent } from 'vue'
import { computed, unref, useRoute, useRouter, useStore } from "@nuxtjs/composition-api";
import { storeToRefs } from "pinia";

import { useAmplitude, useAuth, useConfig, useLinkService, useVuetify } from "@/composables";
import { getAmplitudeStore } from "@/store/amplitude";
import { useCreatorShop } from "@/store/creator-shop";
import { useCreatorShopPostsStore } from "@/store/creator-shop-posts";
import { getExperimentsStore } from "@/store/experiments";
import { getLikesStore } from "@/store/likes";
import { getLtksStore } from "@/store/ltks";
import { FEATURE_FLAG_ACTIVE, FLAG_CREATOR_SHOP_SEARCH } from "@/utils/experiments/constants";
import { Actions } from "@/utils/snowplow/enums";
import { createPostImpression } from "@/utils/snowplow/factory";


export default /*#__PURE__*/_defineComponent({
  __name: 'PostFeedItem',
  props: {
  postId: {
    type: String,
    required: true,
  },
  displayName: {
    type: String,
    required: true,
  },
  profileId: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    required: false,
    default: undefined,
  },
  isSearch: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props;



const store = useStore();
const route = useRoute();
const router = useRouter();
const auth = useAuth();
const amplitude = useAmplitude();
const config = useConfig();
const vuetify = useVuetify();
const linkService = useLinkService();
const creatorShopPostsStore = useCreatorShopPostsStore();
const experimentsStore = getExperimentsStore(store);
const ltksStore = getLtksStore(store);
const { posts } = storeToRefs(creatorShopPostsStore);

const isCreatorShopSearchEnabled = computed(
  () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
);
const isLegacySearch = computed(() => !isCreatorShopSearchEnabled.value && props.isSearch);
const legacyPost = computed(() => posts?.value[props.postId]);
const post = computed(() => ltksStore.posts[props.postId]);
const ltk = computed(() => ltksStore.ltks[props.postId]);

const isPinned = computed(() => {
  const legacyPostValue = legacyPost.value;
  const postValue = post.value;
  const isLegacySearchValue = isLegacySearch.value;
  if (isLegacySearchValue) {
    return Boolean(legacyPostValue?.pinnedAt);
  }
  return Boolean(postValue?.pinnedAt);
});
const isVideo = computed(() => {
  const isLegacySearchValue = isLegacySearch.value;
  const legacyMediaType = legacyPost.value?.mediaType;
  const videoMediaId = ltk.value?.videoMediaId;
  return isLegacySearchValue ? legacyMediaType === "video" : Boolean(videoMediaId);
});
const isLiked = computed(() => Boolean(getLikesStore(store).likes[props.postId]));
const referringComponent = computed(() => amplitude?.getReferringComponentByPage(route.value.name) ?? "");
const HERO_IMAGE_DEFAULT_WIDTH = 450;
const HERO_IMAGE_HALF_WIDTH = 225;
const optimizedWidth = computed(() => (vuetify.breakpoint.xsOnly ? HERO_IMAGE_HALF_WIDTH : HERO_IMAGE_DEFAULT_WIDTH));

const aspectRatio = computed(() => {
  const isLegacySearchValue = isLegacySearch.value;
  const legacyPostValue = legacyPost.value;
  const postValue = ltk.value;
  if (isLegacySearchValue) {
    return legacyPostValue?.heroImageWidth !== undefined && legacyPostValue.heroImageHeight !== undefined
      ? legacyPostValue.heroImageWidth / legacyPostValue.heroImageHeight
      : 1;
  }
  return typeof postValue?.heroImageWidth === "number" && typeof postValue.heroImageHeight === "number"
    ? postValue.heroImageWidth / postValue.heroImageHeight
    : 1;
});

const caption = computed(() => {
  const isLegacySearchValue = isLegacySearch.value;
  const legacyCaption = legacyPost.value?.caption;
  const postCaption = ltk.value?.caption;
  return isLegacySearchValue ? legacyCaption : postCaption;
});
const heroImageLink = computed(() => {
  const isLegacySearchValue = isLegacySearch.value;

  const legacyPostImageLink = legacyPost.value?.imageLink;
  const postImageLink = ltk.value?.heroImage;
  if (isLegacySearchValue) {
    return legacyPostImageLink
      ? linkService.getImageLink({
          url: legacyPostImageLink,
          width: unref(optimizedWidth).toString(),
          crop: false,
          useDefaultDpr: false,
        })
      : "";
  }
  return postImageLink
    ? linkService.getImageLink({
        url: postImageLink,
        width: unref(optimizedWidth).toString(),
        crop: false,
        useDefaultDpr: false,
      })
    : "";
});

const heroImageSrcSet = computed(() =>
  heroImageLink.value
    ? `${heroImageLink.value}&dpr=1 1x, ${heroImageLink.value}&dpr=2 2x, ${heroImageLink.value}&dpr=3 3x`
    : "",
);

const calculatedHeight = computed(() => unref(optimizedWidth) * (1 / aspectRatio.value));

const onFavoriteChange = async () => {
  const postId = props.postId;
  const likesStore = getLikesStore(store);
  const creatorShopTab = useCreatorShop().getLastSeenShopTab;

  if (auth.loggedIn) {
    const payload: LtKFavoriteProps = {
      "ltk-id": postId,
      "profile-id": props.profileId,
      "referring-component": referringComponent.value,
      method: "CLICK",
    };

    if (creatorShopTab) {
      payload["creator-shop-tab"] = creatorShopTab;
    }

    if (isLiked.value) {
      await likesStore.deleteLike({ ltkId: postId });
      amplitude?.sendEvent("/unlike", payload);
    } else {
      await likesStore.createLike({ ltkId: postId });
      amplitude?.sendEvent("/like", payload);
    }
  } else {
    localStorage.setItem("favorite-post", postId);
    getLikesStore(store).setLoggedOutFavLtkId(postId);
    getAmplitudeStore(store).setReferringComponent(referringComponent.value);
    router.push({ path: "/signup", query: { "shopltk-signup-prompt": "favorite-post" } });
  }
};

const onClick = (event: MouseEvent, navigate: (event: MouseEvent) => void) => {
  if (props.isSearch) {
    amplitude?.sendEvent("/search/tap", {
      "click-position": props.position + 1,
      "content-id": props.postId,
      "content-type": "post",
      index: `${config.env}-ltk-shop-posts`,
      "referring-component": "creator shop post feed",
      "search-experience": "matching-posts-search",
    });
  }
  useCreatorShop().setReferringShopTab();
  navigate(event);
};

const postImpressionPayload = computed(() =>
  createPostImpression(Actions.View, {
    id: props.postId,
    creator_id: props.profileId,
    position: props.position,
  }),
);

return { __sfc: true,props, store, route, router, auth, amplitude, config, vuetify, linkService, creatorShopPostsStore, experimentsStore, ltksStore, posts, isCreatorShopSearchEnabled, isLegacySearch, legacyPost, post, ltk, isPinned, isVideo, isLiked, referringComponent, HERO_IMAGE_DEFAULT_WIDTH, HERO_IMAGE_HALF_WIDTH, optimizedWidth, aspectRatio, caption, heroImageLink, heroImageSrcSet, calculatedHeight, onFavoriteChange, onClick, postImpressionPayload }
}

})